/**
 * Folder Uploader for CoandaView
 * Handles uploading entire folders (OpenFOAM cases) to the server
 */

class FolderUploader {
  constructor(viewer) {
    this.viewer = viewer;
    this.apiBase = window.location.origin.includes('localhost')
      ? 'http://localhost:8082'
      : '';
  }

  /**
   * Upload a folder (directory) to the server
   * @param {FileList} files - Files from folder input
   * @param {string} displayName - Optional friendly name for the case
   * @returns {Promise<Object>} Upload result with case_path
   */
  async uploadFolder(files, displayName = null) {
    if (!files || files.length === 0) {
      throw new Error('No files selected');
    }

    // Check file count limit
    const MAX_FILES = 10000;
    if (files.length > MAX_FILES) {
      throw new Error(`Too many files. Maximum allowed: ${MAX_FILES} files. Your folder contains ${files.length} files.`);
    }

    // Reset progress hidden flag for new upload
    this.progressHidden = false;

    // Show progress
    this.showProgress(0, `Preparing to upload ${files.length} files...`);

    // Use parallel batch uploads for better performance
    // NOTE: Browsers limit to ~6 connections per domain, so PARALLEL_CONNECTIONS > 6 queues requests
    // Larger batches = fewer HTTP requests = less overhead = faster uploads
    const PARALLEL_CONNECTIONS = 6; // Match browser's actual connection limit
    const BATCH_SIZE_BYTES = 20 * 1024 * 1024; // 20 MB per batch - optimal for 130ms RTT
    const MAX_FILES_PER_BATCH = 100; // Reduced to prevent overwhelming S3 with too many concurrent uploads
    const COMPRESS_TEXT_FILES = true; // Compress text files with gzip (OpenFOAM files compress ~80%)
    const CHUNK_SIZE = 20 * 1024 * 1024; // 20 MB chunks for large files
    const LARGE_FILE_THRESHOLD = 20 * 1024 * 1024; // Files > 20MB use chunked upload

    // Check if CompressionStream is available (modern browsers)
    const canCompress = COMPRESS_TEXT_FILES && typeof CompressionStream !== 'undefined';

    // Text file extensions that benefit from compression
    const TEXT_EXTENSIONS = new Set([
      '', // OpenFOAM files often have no extension (0/, constant/, system/)
      'txt', 'dat', 'csv', 'json', 'xml', 'log',
      'py', 'js', 'cpp', 'c', 'h', 'hpp',
      'foam', 'obj', 'stl', 'vtk', 'vtp', 'vtu',
      // EnSight ASCII files (compress very well, ~40-60% reduction)
      'case', 'geo', 'vel', 'sos'
    ]);

    // Check if extension should be compressed
    const shouldCompress = (ext) => {
      if (TEXT_EXTENSIONS.has(ext)) return true;
      // EnSight scalar fields: scl, scl1, scl2, ..., scl99, etc.
      if (/^scl\d*$/.test(ext)) return true;
      return false;
    };

    // Compress a file using gzip
    const compressFile = async (file) => {
      // Skip small files (compression overhead not worth it)
      if (file.size < 1024) return { file, compressed: false };

      // Check extension - files with no dot (OpenFOAM mesh files like 'points', 'U') have empty extension
      const parts = file.name.split('.');
      const ext = parts.length > 1 ? parts.pop()?.toLowerCase() : '';
      if (!shouldCompress(ext)) return { file, compressed: false };

      try {
        const stream = file.stream().pipeThrough(new CompressionStream('gzip'));
        const compressedBlob = await new Response(stream).blob();

        // Only use compressed if it's actually smaller
        if (compressedBlob.size < file.size * 0.9) {
          const compressedFile = new File(
            [compressedBlob],
            file.webkitRelativePath || file.name,
            { type: 'application/gzip' }
          );
          // Preserve webkitRelativePath
          Object.defineProperty(compressedFile, 'webkitRelativePath', {
            value: file.webkitRelativePath,
            writable: false
          });
          return { file: compressedFile, compressed: true, originalSize: file.size };
        }
      } catch (e) {
        console.warn('Compression failed for', file.name, e);
      }
      return { file, compressed: false };
    };

    // Generate upload_id client-side so ALL batches can start in parallel immediately
    const uploadId = crypto.randomUUID().slice(0, 8);

    // Pre-process files: compress text files and calculate actual transfer size
    this.showProgress(0, `Compressing ${files.length} files...`);

    const originalSize = Array.from(files).reduce((sum, f) => sum + f.size, 0);
    const processedFiles = [];
    let totalCompressed = 0;
    let compressionSavings = 0;

    // Process files in parallel batches for faster compression
    const COMPRESS_BATCH = 50;
    const fileArray = Array.from(files);

    for (let i = 0; i < fileArray.length; i += COMPRESS_BATCH) {
      const batch = fileArray.slice(i, i + COMPRESS_BATCH);
      const results = await Promise.all(batch.map(async (file) => {
        if (canCompress) {
          const result = await compressFile(file);
          if (result.compressed) {
            totalCompressed++;
            compressionSavings += result.originalSize - result.file.size;
            return { file: result.file, compressed: true, relativePath: file.webkitRelativePath || file.name };
          }
        }
        return { file, compressed: false, relativePath: file.webkitRelativePath || file.name };
      }));
      processedFiles.push(...results);

      // Update compression progress
      const compressPercent = Math.round((processedFiles.length / fileArray.length) * 100);
      this.showProgress(0, `Compressing files... ${compressPercent}%`);
    }

    // Calculate actual transfer size after compression
    const totalBytes = processedFiles.reduce((sum, p) => sum + p.file.size, 0);
    const compressionRatio = originalSize > 0 ? ((1 - totalBytes / originalSize) * 100).toFixed(0) : 0;

    if (totalCompressed > 0) {
      console.log(`🗜️ Compressed ${totalCompressed} files: ${this.formatBytes(originalSize)} → ${this.formatBytes(totalBytes)} (${compressionRatio}% smaller)`);
    }

    // Separate large files (need chunking) from small files (batch upload)
    const largeFiles = processedFiles.filter(item => item.file.size > LARGE_FILE_THRESHOLD);
    const smallFiles = processedFiles.filter(item => item.file.size <= LARGE_FILE_THRESHOLD);

    if (largeFiles.length > 0) {
      console.log(`📦 ${largeFiles.length} large files will use chunked upload: ${largeFiles.map(f => `${f.relativePath} (${this.formatBytes(f.file.size)})`).join(', ')}`);
    }

    // Split small files into batches by SIZE (not count) for optimal RTT handling
    // With 130ms RTT, we want ~20MB batches to keep TCP pipe full
    const batches = [];
    let currentBatch = [];
    let currentBatchSize = 0;

    for (const item of smallFiles) {
      // Start new batch if adding this file would exceed size limit OR file count limit
      if (currentBatch.length > 0 &&
          (currentBatchSize + item.file.size > BATCH_SIZE_BYTES || currentBatch.length >= MAX_FILES_PER_BATCH)) {
        batches.push(currentBatch);
        currentBatch = [];
        currentBatchSize = 0;
      }
      currentBatch.push(item);
      currentBatchSize += item.file.size;
    }
    // Don't forget the last batch
    if (currentBatch.length > 0) {
      batches.push(currentBatch);
    }

    // Log upload plan
    const smallFilesSize = smallFiles.reduce((sum, f) => sum + f.file.size, 0);
    const largeFilesSize = largeFiles.reduce((sum, f) => sum + f.file.size, 0);
    const avgBatchSize = batches.length > 0 ? smallFilesSize / batches.length : 0;

    console.log(`📦 Upload plan (uploadId: ${uploadId}):`);
    console.log(`   - ${smallFiles.length} small files (${this.formatBytes(smallFilesSize)}) in ${batches.length} batches (~${this.formatBytes(avgBatchSize)}/batch)`);
    if (largeFiles.length > 0) {
      const totalChunks = largeFiles.reduce((sum, f) => sum + Math.ceil(f.file.size / CHUNK_SIZE), 0);
      console.log(`   - ${largeFiles.length} large files (${this.formatBytes(largeFilesSize)}) in ${totalChunks} chunks`);
    }
    console.log(`   - ${PARALLEL_CONNECTIONS} parallel connections`);

    try {
      let uploadedFiles = [];

      // Abort flag - when set, all uploads stop immediately
      let aborted = false;
      let abortError = null;

      // Track progress for each batch separately to avoid oscillation
      const batchProgress = new Array(batches.length).fill(0);

      const updateProgress = () => {
        if (aborted) return; // Don't update progress if aborted
        const totalProgress = batchProgress.reduce((sum, p) => sum + p, 0);
        const overallPercent = Math.min(99, Math.round((totalProgress / totalBytes) * 100));
        this.showProgress(overallPercent, `${this.formatBytes(totalProgress)} / ${this.formatBytes(totalBytes)}`);
      };

      // Retry configuration
      const MAX_RETRIES = 3;
      const INITIAL_RETRY_DELAY = 1000; // 1 second

      // Upload batches in parallel with controlled concurrency and retry logic
      const uploadBatch = async (batch, batchIndex) => {
        // Check abort flag before starting
        if (aborted) {
          throw abortError || new Error('Upload aborted');
        }

        // Calculate this batch's total size for progress tracking
        const batchTotalSize = batch.reduce((sum, item) => sum + item.file.size, 0);

        // Retry loop with exponential backoff
        let lastError = null;
        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
          if (aborted) {
            throw abortError || new Error('Upload aborted');
          }

          // Build FormData fresh for each attempt (can't reuse after send)
          const formData = new FormData();
          for (const item of batch) {
            const filename = item.compressed ? item.relativePath + '.gz' : item.relativePath;
            formData.append('files', item.file, filename);
          }
          if (batchIndex === 0 && displayName && displayName.trim()) {
            formData.append('display_name', displayName.trim());
          }
          formData.append('upload_id', uploadId);

          try {
            const result = await new Promise((resolve, reject) => {
              const xhr = new XMLHttpRequest();

              xhr.upload.addEventListener('progress', (e) => {
                if (aborted) return;
                if (e.lengthComputable) {
                  batchProgress[batchIndex] = e.loaded;
                  updateProgress();
                }
              });

              xhr.open('POST', `${this.apiBase}/api/upload-folder`);

              const token = localStorage.getItem('coandacloud_token');
              if (token) {
                xhr.setRequestHeader('Authorization', `Bearer ${token}`);
              }

              xhr.onload = () => {
                if (aborted) {
                  reject(abortError || new Error('Upload aborted'));
                  return;
                }

                if (xhr.status >= 200 && xhr.status < 300) {
                  const result = JSON.parse(xhr.responseText);
                  batchProgress[batchIndex] = batchTotalSize;
                  updateProgress();
                  if (result.data && result.data.uploaded_files) {
                    uploadedFiles.push(...result.data.uploaded_files);
                  }
                  resolve(result);
                } else {
                  let message = `Upload failed: ${xhr.statusText}`;
                  let isQuotaExceeded = false;
                  let isRetryable = xhr.status >= 500 || xhr.status === 429;
                  try {
                    const errorData = JSON.parse(xhr.responseText);
                    if (errorData?.detail) {
                      message = errorData.detail;
                    }
                    if (xhr.status === 413 || (errorData?.detail && errorData.detail.includes('quota exceeded'))) {
                      isQuotaExceeded = true;
                      isRetryable = false;
                    }
                  } catch (err) {}

                  const error = new Error(message);
                  error.isQuotaExceeded = isQuotaExceeded;
                  error.isRetryable = isRetryable;

                  if (isQuotaExceeded) {
                    aborted = true;
                    abortError = error;
                    console.error('[QUOTA] Storage quota exceeded - aborting all uploads');
                  }

                  reject(error);
                }
              };

              xhr.onerror = () => {
                const error = new Error('Network error during upload');
                error.isRetryable = true;
                reject(error);
              };

              xhr.send(formData);
            });

            return result; // Success - return immediately
          } catch (error) {
            lastError = error;

            // Don't retry quota errors or non-retryable errors
            if (error.isQuotaExceeded || !error.isRetryable) {
              throw error;
            }

            // If we have retries left, wait and try again
            if (attempt < MAX_RETRIES) {
              const delay = INITIAL_RETRY_DELAY * Math.pow(2, attempt);
              console.warn(`⚠️ Batch ${batchIndex + 1} failed (attempt ${attempt + 1}/${MAX_RETRIES + 1}): ${error.message} - retrying in ${delay}ms...`);
              await new Promise(r => setTimeout(r, delay));
              // Reset progress for this batch before retry
              batchProgress[batchIndex] = 0;
            }
          }
        }

        // All retries exhausted
        console.error(`❌ Batch ${batchIndex + 1} failed after ${MAX_RETRIES + 1} attempts`);
        throw lastError;
      };

      // Upload a single chunk of a large file (with retry)
      const uploadChunk = async (fileItem, chunkIndex, totalChunks, chunkBlob, chunkProgressCallback) => {
        // Check abort flag before starting
        if (aborted) {
          throw abortError || new Error('Upload aborted');
        }

        let lastError = null;
        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
          if (aborted) {
            throw abortError || new Error('Upload aborted');
          }

          // Build FormData fresh for each attempt
          const formData = new FormData();
          formData.append('chunk', chunkBlob, `chunk_${chunkIndex}`);
          formData.append('upload_id', uploadId);
          formData.append('file_path', fileItem.relativePath);
          formData.append('chunk_index', chunkIndex);
          formData.append('total_chunks', totalChunks);
          formData.append('file_size', fileItem.file.size);
          formData.append('is_compressed', fileItem.compressed ? 'true' : 'false');

          try {
            const result = await new Promise((resolve, reject) => {
              const xhr = new XMLHttpRequest();

              xhr.upload.addEventListener('progress', (e) => {
                if (aborted) return;
                if (e.lengthComputable && chunkProgressCallback) {
                  chunkProgressCallback(e.loaded);
                }
              });

              xhr.open('POST', `${this.apiBase}/api/upload-chunk`);

              const token = localStorage.getItem('coandacloud_token');
              if (token) {
                xhr.setRequestHeader('Authorization', `Bearer ${token}`);
              }

              xhr.onload = () => {
                if (aborted) {
                  reject(abortError || new Error('Upload aborted'));
                  return;
                }

                if (xhr.status >= 200 && xhr.status < 300) {
                  resolve(JSON.parse(xhr.responseText));
                } else {
                  let isQuotaExceeded = false;
                  let isRetryable = xhr.status >= 500 || xhr.status === 429;
                  let message = `Chunk upload failed: ${xhr.statusText}`;
                  try {
                    const errorData = JSON.parse(xhr.responseText);
                    if (errorData?.detail) {
                      message = errorData.detail;
                    }
                    if (xhr.status === 413 || (errorData?.detail && errorData.detail.includes('quota exceeded'))) {
                      isQuotaExceeded = true;
                      isRetryable = false;
                    }
                  } catch (err) {}

                  const error = new Error(message);
                  error.isQuotaExceeded = isQuotaExceeded;
                  error.isRetryable = isRetryable;

                  if (isQuotaExceeded) {
                    aborted = true;
                    abortError = error;
                    console.error('[QUOTA] Storage quota exceeded during chunk upload - aborting');
                  }

                  reject(error);
                }
              };

              xhr.onerror = () => {
                const error = new Error('Network error during chunk upload');
                error.isRetryable = true;
                reject(error);
              };

              xhr.send(formData);
            });

            return result; // Success
          } catch (error) {
            lastError = error;

            if (error.isQuotaExceeded || !error.isRetryable) {
              throw error;
            }

            if (attempt < MAX_RETRIES) {
              const delay = INITIAL_RETRY_DELAY * Math.pow(2, attempt);
              console.warn(`⚠️ Chunk ${chunkIndex + 1}/${totalChunks} failed (attempt ${attempt + 1}/${MAX_RETRIES + 1}): ${error.message} - retrying in ${delay}ms...`);
              await new Promise(r => setTimeout(r, delay));
            }
          }
        }

        console.error(`❌ Chunk ${chunkIndex + 1}/${totalChunks} failed after ${MAX_RETRIES + 1} attempts`);
        throw lastError;
      };

      // Upload a large file using parallel chunks
      const uploadLargeFile = async (fileItem, fileProgressCallback) => {
        // Check abort flag before starting
        if (aborted) {
          throw abortError || new Error('Upload aborted');
        }

        const file = fileItem.file;
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        const chunkProgress = new Array(totalChunks).fill(0);

        const updateFileProgress = () => {
          if (aborted) return;
          const uploaded = chunkProgress.reduce((sum, p) => sum + p, 0);
          fileProgressCallback(uploaded);
        };

        console.log(`🔪 Splitting ${fileItem.relativePath} into ${totalChunks} chunks of ${this.formatBytes(CHUNK_SIZE)}`);

        // Create all chunk upload promises
        const chunkPromises = [];
        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const chunkBlob = file.slice(start, end);
          const chunkIdx = i;

          chunkPromises.push({
            index: i,
            upload: () => uploadChunk(fileItem, chunkIdx, totalChunks, chunkBlob, (loaded) => {
              if (!aborted) {
                chunkProgress[chunkIdx] = loaded;
                updateFileProgress();
              }
            })
          });
        }

        // Upload chunks with parallel connections
        let nextChunkIndex = 0;
        let activeChunks = 0;
        const chunkResults = [];

        await new Promise((resolveFile, rejectFile) => {
          let rejected = false;

          const startNextChunk = () => {
            // Don't start new chunks if aborted
            if (aborted || rejected) {
              if (activeChunks === 0 && !rejected) {
                rejected = true;
                rejectFile(abortError || new Error('Upload aborted'));
              }
              return;
            }

            while (activeChunks < PARALLEL_CONNECTIONS && nextChunkIndex < chunkPromises.length && !aborted) {
              const chunkInfo = chunkPromises[nextChunkIndex++];
              activeChunks++;

              chunkInfo.upload()
                .then(result => {
                  if (aborted || rejected) {
                    activeChunks--;
                    return;
                  }

                  chunkProgress[chunkInfo.index] = CHUNK_SIZE; // Mark as complete
                  updateFileProgress();
                  chunkResults.push(result);
                  activeChunks--;

                  if (nextChunkIndex < chunkPromises.length && !aborted) {
                    startNextChunk();
                  } else if (activeChunks === 0 && !rejected) {
                    resolveFile();
                  }
                })
                .catch(err => {
                  activeChunks--;
                  if (!rejected) {
                    rejected = true;
                    rejectFile(err);
                  }
                });
            }

            if (nextChunkIndex >= chunkPromises.length && activeChunks === 0 && !rejected) {
              resolveFile();
            }
          };

          startNextChunk();
        });

        console.log(`✅ Large file ${fileItem.relativePath} uploaded (${totalChunks} chunks)`);
        return chunkResults;
      };

      // Process batches with sliding window - start new batch as soon as one completes
      const results = [];
      let nextBatchIndex = 0;
      let activeCount = 0;
      const startTime = performance.now();

      // First, upload small files in batches (this also creates the folder)
      // If no small files, we need to create an empty batch to initialize the folder
      if (batches.length === 0 && largeFiles.length > 0) {
        // Create folder with an empty placeholder batch
        console.log('📁 Creating folder for chunked uploads...');
        const formData = new FormData();
        formData.append('upload_id', uploadId);
        if (displayName && displayName.trim()) {
          formData.append('display_name', displayName.trim());
        }
        // Create a tiny placeholder file to initialize the folder
        const placeholder = new Blob([''], { type: 'text/plain' });
        formData.append('files', placeholder, '.chunked_upload_marker');

        const response = await fetch(`${this.apiBase}/api/upload-folder`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('coandacloud_token')}`
          },
          body: formData
        });

        if (!response.ok) {
          throw new Error('Failed to initialize folder for chunked upload');
        }

        const result = await response.json();
        results.push(result);
        console.log('📁 Folder created for chunked uploads');
      }

      if (batches.length > 0) {
        await new Promise((resolveAll, rejectAll) => {
          let rejected = false;

          const startNextBatch = () => {
            // Don't start new batches if aborted or already rejected
            if (aborted || rejected) {
              if (activeCount === 0 && !rejected) {
                rejected = true;
                rejectAll(abortError || new Error('Upload aborted'));
              }
              return;
            }

            while (activeCount < PARALLEL_CONNECTIONS && nextBatchIndex < batches.length && !aborted) {
              const batchIndex = nextBatchIndex++;
              activeCount++;
              const batchStartTime = performance.now();
              const thisBatchSize = batches[batchIndex].reduce((sum, item) => sum + item.file.size, 0);
              console.log(`🚀 Starting batch ${batchIndex + 1}/${batches.length} (${(thisBatchSize/1024/1024).toFixed(1)} MB, ${batches[batchIndex].length} files, active: ${activeCount})`);

              uploadBatch(batches[batchIndex], batchIndex)
                .then(result => {
                  if (aborted || rejected) {
                    activeCount--;
                    return;
                  }

                  const batchTime = ((performance.now() - batchStartTime) / 1000).toFixed(1);
                  const batchSize = batches[batchIndex].reduce((sum, item) => sum + item.file.size, 0);
                  const speed = (batchSize / 1024 / 1024) / (batchTime || 1);
                  console.log(`✅ Batch ${batchIndex + 1} done in ${batchTime}s (${speed.toFixed(1)} MB/s)`);

                  results.push(result);
                  activeCount--;
                  if (nextBatchIndex < batches.length && !aborted) {
                    startNextBatch();
                  } else if (activeCount === 0 && !rejected) {
                    resolveAll();
                  }
                })
                .catch(err => {
                  activeCount--;
                  if (!rejected) {
                    rejected = true;
                    rejectAll(err);
                  }
                });
            }

            // If no batches to process and none active, we're done
            if (nextBatchIndex >= batches.length && activeCount === 0 && !rejected) {
              resolveAll();
            }
          };

          startNextBatch();
        });
      }

      // Now upload large files using chunked upload (only if not already aborted)
      if (largeFiles.length > 0 && !aborted) {
        console.log(`📤 Starting chunked upload for ${largeFiles.length} large files...`);

        // Track progress for large files (smallFilesSize already defined above)
        const largeFilesProgress = new Array(largeFiles.length).fill(0);

        const updateLargeFileProgress = () => {
          if (aborted) return;
          const largeUploaded = largeFilesProgress.reduce((sum, p) => sum + p, 0);
          const totalUploaded = smallFilesSize + largeUploaded;
          const overallPercent = Math.min(99, Math.round((totalUploaded / totalBytes) * 100));
          this.showProgress(overallPercent, `${this.formatBytes(totalUploaded)} / ${this.formatBytes(totalBytes)}`);
        };

        // Upload large files in parallel (each file's chunks are also parallel)
        let nextLargeFileIndex = 0;
        let activeLargeFiles = 0;

        await new Promise((resolveAll, rejectAll) => {
          let rejected = false;

          const startNextLargeFile = () => {
            // Don't start new files if aborted
            if (aborted || rejected) {
              if (activeLargeFiles === 0 && !rejected) {
                rejected = true;
                rejectAll(abortError || new Error('Upload aborted'));
              }
              return;
            }

            // Limit to 2 large files at a time (each uses PARALLEL_CONNECTIONS for chunks)
            while (activeLargeFiles < 2 && nextLargeFileIndex < largeFiles.length && !aborted) {
              const fileIndex = nextLargeFileIndex++;
              const fileItem = largeFiles[fileIndex];
              activeLargeFiles++;

              const fileStartTime = performance.now();
              console.log(`🚀 Starting chunked upload: ${fileItem.relativePath} (${this.formatBytes(fileItem.file.size)})`);

              uploadLargeFile(fileItem, (uploaded) => {
                if (!aborted) {
                  largeFilesProgress[fileIndex] = uploaded;
                  updateLargeFileProgress();
                }
              })
                .then(() => {
                  if (aborted || rejected) {
                    activeLargeFiles--;
                    return;
                  }

                  const fileTime = ((performance.now() - fileStartTime) / 1000).toFixed(1);
                  const speed = (fileItem.file.size / 1024 / 1024) / (fileTime || 1);
                  console.log(`✅ Large file done: ${fileItem.relativePath} in ${fileTime}s (${speed.toFixed(1)} MB/s)`);

                  largeFilesProgress[fileIndex] = fileItem.file.size;
                  updateLargeFileProgress();
                  activeLargeFiles--;

                  if (nextLargeFileIndex < largeFiles.length && !aborted) {
                    startNextLargeFile();
                  } else if (activeLargeFiles === 0 && !rejected) {
                    resolveAll();
                  }
                })
                .catch(err => {
                  activeLargeFiles--;
                  if (!rejected) {
                    rejected = true;
                    rejectAll(err);
                  }
                });
            }

            if (nextLargeFileIndex >= largeFiles.length && activeLargeFiles === 0 && !rejected) {
              resolveAll();
            }
          };

          startNextLargeFile();
        });
      }

      const totalTime = ((performance.now() - startTime) / 1000).toFixed(1);
      const overallSpeed = (totalBytes / 1024 / 1024) / (totalTime || 1);
      console.log(`🏁 Upload complete: ${this.formatBytes(totalBytes)} in ${totalTime}s (${overallSpeed.toFixed(1)} MB/s overall)`);

      this.showProgress(100, 'Upload complete!');

      // Return combined result from first batch (which has the main info)
      // Note: caller is responsible for calling hideProgress() before showing success modal
      const firstResult = results[0];
      if (firstResult && firstResult.data) {
        firstResult.data.file_count = files.length;
      }

      return firstResult;

    } catch (error) {
      this.hideProgress();
      throw error;
    }
  }

  /**
   * Show upload progress modal
   */
  showProgress(percent, message) {
    // Don't show progress if it was explicitly hidden (e.g., due to error)
    if (this.progressHidden) return;

    // Create modal if it doesn't exist
    if (!this.progressModal) {
      const modalHTML = `
        <div id="uploadProgressModal" style="
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
        ">
          <div style="
            background: var(--bg-panel, #1c1c1e);
            border-radius: 12px;
            padding: 32px;
            max-width: 450px;
            width: 90%;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
          ">
            <div style="
              width: 64px;
              height: 64px;
              background: rgba(33, 150, 243, 0.2);
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              margin: 0 auto 20px;
            ">
              <img src="static/assets/icons/upload.svg" alt="Upload" width="32" height="32" style="filter: brightness(0) saturate(100%) invert(52%) sepia(98%) saturate(1234%) hue-rotate(190deg) brightness(101%) contrast(95%);">
            </div>

            <h2 style="
              color: var(--text-primary, #fff);
              margin: 0 0 12px 0;
              font-size: 20px;
              font-weight: 600;
              text-align: center;
            ">Uploading...</h2>

            <p id="uploadProgressStatus" style="
              color: var(--text-secondary, #a8a8a8);
              margin: 0 0 20px 0;
              font-size: 14px;
              text-align: center;
            ">${message}</p>

            <div style="
              display: flex;
              justify-content: space-between;
              margin-bottom: 8px;
            ">
              <span style="color: var(--text-secondary, #a8a8a8); font-size: 13px;">Progress</span>
              <span id="uploadProgressPercent" style="color: #2196F3; font-weight: 600; font-size: 13px;">${percent}%</span>
            </div>

            <div style="
              width: 100%;
              height: 8px;
              background: var(--bg-darker, #0a0a0a);
              border-radius: 4px;
              overflow: hidden;
            ">
              <div id="uploadProgressBar" style="
                width: ${percent}%;
                height: 100%;
                background: linear-gradient(90deg, #2196F3, #64B5F6);
                border-radius: 4px;
                transition: width 0.3s ease;
              "></div>
            </div>
          </div>
        </div>
      `;

      this.progressModal = document.createElement('div');
      this.progressModal.innerHTML = modalHTML;
      document.body.appendChild(this.progressModal);
    } else {
      // Update existing modal
      const statusEl = document.getElementById('uploadProgressStatus');
      const percentEl = document.getElementById('uploadProgressPercent');
      const barEl = document.getElementById('uploadProgressBar');

      if (statusEl) statusEl.textContent = message;
      if (percentEl) percentEl.textContent = `${percent}%`;
      if (barEl) barEl.style.width = `${percent}%`;
    }
  }

  /**
   * Hide upload progress modal
   */
  hideProgress() {
    this.progressHidden = true; // Prevent recreation by stray callbacks
    if (this.progressModal) {
      this.progressModal.remove();
      this.progressModal = null;
    }
  }

  /**
   * Format bytes to human readable string
   */
  formatBytes(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Show success modal after successful upload
   */
  static showUploadSuccess(uploadInfo) {
    const { caseName, fileCount, isOpenFOAM, storageUsed, storageTotal } = uploadInfo;

    // Calculate storage percentage
    const storagePercent = storageTotal > 0 ? Math.round((storageUsed / storageTotal) * 100) : 0;
    const storageUsedGB = (storageUsed / (1024 * 1024 * 1024)).toFixed(2);
    const storageTotalGB = (storageTotal / (1024 * 1024 * 1024)).toFixed(2);

    const modalHTML = `
      <div id="successModal" style="
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.7);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10000;
        animation: fadeIn 0.2s ease;
      ">
        <div style="
          background: var(--bg-panel, #1c1c1e);
          border-radius: 12px;
          padding: 32px;
          max-width: 500px;
          width: 90%;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
          animation: slideUp 0.3s ease;
        ">
          <div style="
            width: 64px;
            height: 64px;
            background: rgba(48, 209, 88, 0.2);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 20px;
          ">
            <img src="static/assets/icons/check.svg" alt="" width="32" height="32" style="filter: brightness(0) saturate(100%) invert(76%) sepia(52%) saturate(497%) hue-rotate(88deg) brightness(95%) contrast(89%);">
          </div>

          <h2 style="
            color: var(--text-primary, #fff);
            margin: 0 0 12px 0;
            font-size: 24px;
            font-weight: 600;
            text-align: center;
          ">Upload Successful!</h2>

          <p style="
            color: var(--text-secondary, #a8a8a8);
            margin: 0 0 24px 0;
            line-height: 1.5;
            text-align: center;
          ">"${caseName}" uploaded successfully with ${fileCount} file${fileCount !== 1 ? 's' : ''}.</p>

          <div style="
            background: var(--bg-darker, #0a0a0a);
            border-radius: 8px;
            padding: 16px;
            margin-bottom: 24px;
          ">
            <div style="
              display: flex;
              justify-content: space-between;
              margin-bottom: 8px;
            ">
              <span style="color: var(--text-secondary, #a8a8a8); font-size: 14px;">Storage Used</span>
              <span style="color: var(--text-primary, #fff); font-weight: 600; font-size: 14px;">${storageUsedGB} GB / ${storageTotalGB} GB</span>
            </div>
            <div style="
              width: 100%;
              height: 8px;
              background: var(--bg-panel, #1c1c1e);
              border-radius: 4px;
              overflow: hidden;
            ">
              <div style="
                width: ${storagePercent}%;
                height: 100%;
                background: ${storagePercent > 80 ? 'var(--apple-warning, #ff9500)' : 'var(--apple-accent, #30d158)'};
                transition: width 0.3s ease;
              "></div>
            </div>
          </div>

          <button id="successModalOk" style="
            width: 100%;
            background: var(--apple-accent, #30d158);
            color: white;
            border: none;
            padding: 14px 24px;
            border-radius: 8px;
            font-size: 15px;
            font-weight: 600;
            cursor: pointer;
          ">Got it!</button>
        </div>
      </div>
    `;

    // Add animations
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes slideUp {
        from { transform: translateY(20px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);

    // Add modal to page
    const modalDiv = document.createElement('div');
    modalDiv.innerHTML = modalHTML;
    document.body.appendChild(modalDiv);

    // Handle button click
    document.getElementById('successModalOk').addEventListener('click', () => {
      modalDiv.remove();
      style.remove();
    });

    // Close on background click
    document.getElementById('successModal').addEventListener('click', (e) => {
      if (e.target.id === 'successModal') {
        modalDiv.remove();
        style.remove();
      }
    });

    // Auto-close after 5 seconds
    setTimeout(() => {
      if (modalDiv.parentNode) {
        modalDiv.remove();
        style.remove();
      }
    }, 5000);
  }

  /**
   * Show upgrade prompt when storage quota is exceeded
   */
  static showUpgradePrompt(errorMessage) {
    // Create modal HTML
    const modalHTML = `
      <div id="upgradeModal" style="
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.7);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10000;
      ">
        <div style="
          background: var(--bg-panel, #1c1c1e);
          border-radius: 12px;
          padding: 32px;
          max-width: 500px;
          width: 90%;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
        ">
          <h2 style="
            color: var(--text-primary, #fff);
            margin: 0 0 16px 0;
            font-size: 24px;
            font-weight: 600;
          ">Storage Quota Exceeded</h2>

          <p style="
            color: var(--text-secondary, #a8a8a8);
            margin: 0 0 24px 0;
            line-height: 1.5;
          ">${errorMessage}</p>

          <p style="
            color: var(--text-primary, #fff);
            margin: 0 0 24px 0;
            line-height: 1.5;
          ">To upload more data, please upgrade your storage plan.</p>

          <div style="
            display: flex;
            gap: 12px;
            justify-content: flex-end;
          ">
            <button id="upgradeModalCancel" style="
              background: var(--bg-panel-hover, #2c2c2e);
              color: var(--text-primary, #fff);
              border: 1px solid var(--border-color, #38383a);
              padding: 12px 24px;
              border-radius: 8px;
              font-size: 15px;
              font-weight: 500;
              cursor: pointer;
            ">Cancel</button>
            <button id="upgradeModalConfirm" style="
              background: var(--apple-blue, #007AFF);
              color: white;
              border: none;
              padding: 12px 24px;
              border-radius: 8px;
              font-size: 15px;
              font-weight: 500;
              cursor: pointer;
            ">View Plans</button>
          </div>
        </div>
      </div>
    `;

    // Add modal to page
    const modalDiv = document.createElement('div');
    modalDiv.innerHTML = modalHTML;
    document.body.appendChild(modalDiv);

    // Handle button clicks
    document.getElementById('upgradeModalCancel').addEventListener('click', () => {
      modalDiv.remove();
    });

    document.getElementById('upgradeModalConfirm').addEventListener('click', () => {
      window.location.href = '/pricing.html';
    });

    // Close on background click
    document.getElementById('upgradeModal').addEventListener('click', (e) => {
      if (e.target.id === 'upgradeModal') {
        modalDiv.remove();
      }
    });
  }
}

// Export for use in other scripts
window.FolderUploader = FolderUploader;
